/** Trainingsseite (F-34): Uebungsbibliothek, Plaene und ihre Einheiten. */
import type { ID, ISOInstant, LocalDate } from '@/domain/types';
import type { GruppeId, MuskelId } from '@/domain/training/muskeln';

export type EinheitTyp = 'kraft' | 'ausdauer' | 'zirkel' | 'mobility' | 'skill';

export const EINHEIT_TYPEN: Record<EinheitTyp, { label: string }> = {
  kraft: { label: 'Kraft' },
  ausdauer: { label: 'Ausdauer / Lauf' },
  zirkel: { label: 'Zirkel / Hyrox' },
  mobility: { label: 'Mobility / Dehnen' },
  skill: { label: 'Skill' },
};

/** Standardwerte einer Uebung - Vorschlag beim Einplanen. */
export interface UebungsStandard {
  saetze?: number;
  wdh?: number;
  wdhBis?: number;
  gewicht?: number;
  dauerSek?: number;
  pauseSek?: number;
}

export interface Uebung {
  id: ID;
  name: string;
  typ: EinheitTyp;
  hauptmuskeln: MuskelId[];
  hilfsmuskeln: MuskelId[];
  standard: UebungsStandard;
  /** Zum Rotieren oder Ersetzen (z. B. im Live-Modus). */
  alternativen: ID[];
  hinweis?: string;
  /** Mit dem eigenen Koerpergewicht (Klimmzug, Liegestuetz) - fuer Rang und Kraftwerte. */
  koerpergewicht?: boolean;
  /** Skill: Fortschrittsstufen von leicht nach schwer. */
  stufen?: string[];
  /** Mitgeliefert (stabile ID) statt selbst angelegt. */
  standardUebung?: boolean;
  archiviert?: boolean;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

export type SatzArt = 'aufwaermen' | 'arbeit';

/** Ein geplanter Satz - Saetze einer Uebung koennen verschieden sein (Pyramide, Aufwaermsatz). */
export interface PlanSatz {
  art?: SatzArt;
  wdh?: number;
  /** Bereich, z. B. 8-12. */
  wdhBis?: number;
  gewicht?: number;
  dauerSek?: number;
}

export interface PlanUebung {
  /** Eindeutig innerhalb der Einheit. */
  id: string;
  uebungId: ID;
  saetze: PlanSatz[];
  pauseSek?: number;
  notiz?: string;
  /** Gleicher Schluessel wie die Nachbarn = Supersatz/Zirkel: Pause erst nach der Gruppe. */
  block?: string;
  /** Skill: aktuelle Stufe. */
  stufe?: string;
}

export type Intensitaet = 'locker' | 'tempo' | 'intervalle' | 'lang';

export const INTENSITAETEN: Record<Intensitaet, string> = {
  locker: 'Locker', tempo: 'Tempo', intervalle: 'Intervalle', lang: 'Langer Lauf',
};

export interface AusdauerPlan {
  dauerMin?: number;
  distanzKm?: number;
  /** Sekunden je Kilometer. */
  paceSek?: number;
  intensitaet?: Intensitaet;
}

export interface ZirkelPlan {
  runden: number;
  /** Arbeitszeit je Station, falls die Station keine eigene Dauer hat. */
  arbeitSek?: number;
  /** Pause zwischen Stationen. */
  pauseSek?: number;
  /** Pause zwischen Runden. */
  rundenPauseSek?: number;
}

export type Wochentag = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export const WOCHENTAGE: { tag: Wochentag; kurz: string; lang: string }[] = [
  { tag: 1, kurz: 'Mo', lang: 'Montag' }, { tag: 2, kurz: 'Di', lang: 'Dienstag' }, { tag: 3, kurz: 'Mi', lang: 'Mittwoch' },
  { tag: 4, kurz: 'Do', lang: 'Donnerstag' }, { tag: 5, kurz: 'Fr', lang: 'Freitag' }, { tag: 6, kurz: 'Sa', lang: 'Samstag' },
  { tag: 7, kurz: 'So', lang: 'Sonntag' },
];

export type TagArt = 'training' | 'aktiv' | 'ruhe';
export const TAG_ARTEN: Record<TagArt, string> = { training: 'Trainingstag', aktiv: 'Aktive Erholung', ruhe: 'Ruhetag' };

export interface PlanEinheit {
  id: ID;
  planId: ID;
  wochentag: Wochentag;
  /** Bei Wochen im Wechsel bzw. Zyklus: 1..n. Fehlt = jede Woche. */
  woche?: number;
  /** Bruchzahl wie bei den Listen. */
  order: number;
  name: string;
  typ: EinheitTyp;
  /** `HH:mm` */
  uhrzeit?: string;
  /** Manuell gesetzte Dauer - ueberschreibt die Schaetzung. */
  dauerMin?: number;
  /** Gewohnheit, die eine absolvierte Einheit abhakt (E2). */
  habitId?: ID;
  aufwaermen?: string;
  aufwaermenMin?: number;
  uebungen: PlanUebung[];
  ausdauer?: AusdauerPlan;
  zirkel?: ZirkelPlan;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

export interface TrainingsPlan {
  id: ID;
  name: string;
  /** Alle Versionen eines Plans teilen die Familie. */
  familie: ID;
  version: number;
  gueltigAb: LocalDate;
  /** Gesetzt, sobald eine neuere Version gilt. */
  gueltigBis?: LocalDate;
  /** Der Plan, nach dem „Heute“ und der Kalender gehen. */
  aktiv: boolean;
  tage: Record<Wochentag, TagArt>;
  /** 1 = jede Woche gleich, 2 = A/B-Woche, n = Zyklus aus n Wochen. */
  wochen: number;
  /** Montag der ersten Zykluswoche. */
  zyklusStart?: LocalDate;
  /** Aufwaermen je Einheitentyp - gilt, wenn die Einheit kein eigenes hat. */
  aufwaermen?: Partial<Record<EinheitTyp, string>>;
  notiz?: string;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

// --- Protokoll (Stufe B) -------------------------------------------------------

/** Ein tatsaechlich gemachter (oder noch offener) Satz. */
export interface ProtokollSatz {
  art?: SatzArt;
  wdh?: number;
  gewicht?: number;
  dauerSek?: number;
  /** Ziel laut Plan - zur Anzeige neben dem Ist. */
  ziel?: PlanSatz;
  erledigt: boolean;
  zeit?: ISOInstant;
}

export interface ProtokollUebung {
  id: string;
  uebungId: ID;
  /** Getauscht: die urspruenglich geplante Uebung. */
  ersetztVon?: ID;
  uebersprungen?: boolean;
  saetze: ProtokollSatz[];
  pauseSek?: number;
  block?: string;
  stufe?: string;
  notiz?: string;
}

export interface AusdauerIst {
  distanzKm?: number;
  dauerSek?: number;
  puls?: number;
  hoehenmeter?: number;
  intensitaet?: Intensitaet;
}

export type ProtokollStatus = 'laeuft' | 'fertig';

export type Sportart = 'laufen' | 'rad' | 'schwimmen' | 'sonstiges';
export const SPORTARTEN: Record<Sportart, string> = { laufen: 'Laufen', rad: 'Rad', schwimmen: 'Schwimmen', sonstiges: 'Sonstiges' };

export interface TrainingsProtokoll {
  id: ID;
  datum: LocalDate;
  status: ProtokollStatus;
  start?: ISOInstant;
  ende?: ISOInstant;
  planEinheitId?: ID;
  planId?: ID;
  name: string;
  typ: EinheitTyp;
  /** Nicht im Plan, z. B. Fussball oder Radtour. */
  frei?: boolean;
  /** Bei Ausdauer: welche Sportart - nur Laeufe zaehlen fuer Tempo und Laufziele. */
  sportart?: Sportart;
  uebungen: ProtokollUebung[];
  ausdauer?: AusdauerIst;
  /** Von Hand eingetragene Dauer (nachgetragen, frei). */
  dauerMin?: number;
  /** Wie lief es? 1-10, 10 = gut. */
  bewertung?: number;
  /** Wie leicht fiel es? 1-10, 10 = leicht (Leitlinie 6: 10 ist immer gut). */
  leicht?: number;
  notiz?: string;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

// --- Einstellungen (Stufe C) --------------------------------------------------

/** Einstellungen der Trainingsauswertung - eine Zeile (`id: 'standard'`), synchronisiert. */
export interface TrainingsEinstellungen {
  id: 'standard';
  /** Gewuenschte Verteilung nach Zeit, in Prozent je Typ. */
  zielVerteilung?: Partial<Record<EinheitTyp, number>>;
  /** Ziele je Muskelgruppe: Saetze und Trainingstage je Woche (C2). */
  muskelZiele?: Partial<Record<GruppeId, { saetze: number; tage: number }>>;
  /** Ab welchem Verhaeltnis ein Ungleichgewicht gemeldet wird (Standard 1,5). */
  ungleichgewichtSchwelle?: number;
  /** Nach wie vielen Wochen ohne Bestwert eine Uebung als stagnierend gilt (Standard 4). */
  stagnationWochen?: number;
  /** Zuletzt gewaehlte Ansicht der Koerperkarte. */
  kartenAnsicht?: KartenAnsicht;
  /** Grundzeiten der Regeneration in Stunden je Muskelgroesse (C3). */
  regeneration?: { gross: number; mittel: number; klein: number };
  /** Eigene Rang-Schwellen je Uebung (C3). */
  kraftStandards?: Record<string, { art: 'kg' | 'wdh'; stufen: [number, number, number, number, number] }>;
  /** Zuletzt gesehener Rangwert je Gruppe - fuer die Aufstiegsmeldung. */
  raenge?: Partial<Record<GruppeId, number>>;
  /** Bereich, in dem Sportziele liegen (D). */
  sportBereichId?: ID;
  /** Faehigkeiten des Sport-Rads (D), Reihenfolge = Achsen. */
  sportRadKategorien?: SportRadKategorie[];
  /** Training im Kalender (E1). */
  kalender?: KalenderEinstellungen;
  updatedAt: ISOInstant;
}

export type KartenAnsicht = 'volumen' | 'regeneration' | 'stand' | 'heute';

/** Koerpergewicht mit Verlauf (C3) - Grundlage der Kraftraenge. */
export interface KoerperGewicht {
  id: ID;
  datum: LocalDate;
  kg: number;
  createdAt: ISOInstant;
}

/** „Muskelkater/fuehlt sich muede an“ fuer einen Tag (C3). `id` = `datum|muskel`. */
export interface MuskelGefuehl {
  id: string;
  datum: LocalDate;
  muskel: MuskelId;
}

/** Achse des Sport-Rads (F-34 D). */
export interface SportRadKategorie { id: ID; name: string }

export const STANDARD_SPORTRAD: SportRadKategorie[] = [
  { id: 'maximalkraft', name: 'Maximalkraft' },
  { id: 'kraftausdauer', name: 'Kraftausdauer' },
  { id: 'ausdauer', name: 'Ausdauer' },
  { id: 'schnelligkeit', name: 'Schnelligkeit' },
  { id: 'beweglichkeit', name: 'Beweglichkeit' },
  { id: 'skills', name: 'Skills' },
];

/** Selbsteinschaetzung „Wo stehe ich?“ (F-34 D): Ist und Soll 1-10 je Faehigkeit, ein Eintrag je Tag (`id` = Datum). */
export interface SportRadEintrag {
  id: LocalDate;
  datum: LocalDate;
  ist: Record<ID, number>;
  soll: Record<ID, number>;
  /** Soll „in X Wochen“. */
  sollWochen: number;
  notiz?: string;
  updatedAt: ISOInstant;
}

/** Training im Kalender (F-34 E1). */
export interface KalenderEinstellungen {
  /** Uhrzeit fuer Einheiten ohne eigene Uhrzeit, `HH:mm`. */
  uhrzeit: string;
  /** Zeitfenster fuer „auf freie Zeit verschieben“. */
  fenster: { von: string; bis: string };
  /** Den Kalender immer fuer die naechsten `wochen` Wochen gefuellt halten. Standard: aus. */
  rollierend: boolean;
  wochen: number;
}
